/**
 * Persistence helpers for `useMasterDetailSelection` (responsive-detail-mode).
 *
 * Pure module — JSX/React-free so the storage round-trip is unit-testable
 * under the `node --test` strip-types sweep with a fake Storage. All
 * operations are exception-safe: localStorage can throw in private windows
 * and embedded webviews, and a corrupt value must never break a view.
 */

/** Structural subset of `Storage` so tests can pass a plain fake. */
export interface SelectionStorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** Namespaced storage key — mirrors the `tyndo.md.*` MasterDetailLayout convention. */
export function selectionStorageKey(persistKey: string): string {
  return `tyndo.mds.${persistKey}`;
}

/** Read the persisted selection id, or null when absent/unreadable. */
export function readPersistedSelection(
  storage: SelectionStorageLike | undefined,
  persistKey: string,
): string | null {
  if (!storage) return null;
  try {
    return storage.getItem(selectionStorageKey(persistKey));
  } catch {
    return null;
  }
}

/** Persist (or, with null, clear) the selection id. Failures are swallowed. */
export function writePersistedSelection(
  storage: SelectionStorageLike | undefined,
  persistKey: string,
  selectedId: string | null,
): void {
  if (!storage) return;
  try {
    if (selectedId == null) {
      storage.removeItem(selectionStorageKey(persistKey));
    } else {
      storage.setItem(selectionStorageKey(persistKey), selectedId);
    }
  } catch {
    /* ignore — persistence is best-effort */
  }
}
